import { useCallback, useEffect, useMemo, useState } from "react";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import styles from "../index.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { useSelector } from "react-redux";
import { CustomButton } from "../../../../components";

const SyllabusList = () => {
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  //   const academic_year = clientData?.academic_year || "";
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Accept CLASSID from either CLASSID or ID (depending on which page navigated here)
  const CLASSID = location.state?.CLASSID || location.state?.ID || null;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // ================= API CALL =================
  useEffect(() => {
    const fetchHallOfFame = async () => {
      setLoading(true);
      try {
        const response = await AxiosInstance.post("/school/listSyllabus", {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          school_id: school_id,
          class_id: CLASSID,
        });
        const apiData = response.data.data || [];
        // Transform API data to match table structure
        const transformedData = apiData.map((item) => ({
          id: item.id,
          class_id: item.class_id, // ✅ keep class_id on every row
          class: item.domain_id || "-",
          chapter: item.chapter || "-",
          class_name: item.class_name || "-",
          maleCount: item.no_of_boys || 0,
          subject_name: item.subject_name || "-",
          femaleCount: item.no_of_girls || 0,
          totalStudents: (item.no_of_boys || 0) + (item.no_of_girls || 0),
          noOfSection: item.no_of_section || 0,
        }));
        setData(transformedData);
        setRowCount(transformedData.length);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchHallOfFame();
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    apiSearch,
    CLASSID,
    school_id,
  ]);

  // ✅ Pass BOTH SYLLABUSID and CLASSID to view page
  const handleView = useCallback(
    (rowData) => {
      navigate(`/user/syllabusView`, {
        state: {
          SYLLABUSID: rowData.id,
          CLASSID: rowData.class_id || CLASSID,
        },
      });
    },
    [navigate, CLASSID],
  );

  const { translations } = useSelector((state) => state.language);

  // ================= COLUMNS =================
  const columns = useMemo(
    () => [
      {
        accessorKey: "class_name",
        header: translations.labels.class,
        enableSorting: true,
      },
      {
        accessorKey: "subject_name",
        header: translations.labels.subject,
        enableSorting: true,
      },
      {
        accessorKey: "chapter",
        header: translations.labels.chapter,
        enableSorting: true,
      },
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations.labels?.viewStudent || "View Student"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [translations, handleView],
  );

  // ✅ Pass CLASSID to create page, with a guard
  const handleCreateSyllabus = () => {
    if (!CLASSID) {
      alert("Class ID is missing. Please navigate from the class list.");
      return;
    }
    navigate("/user/syllabusCreate", {
      state: {
        CLASSID: CLASSID,
      },
    });
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.header}>
            <h1 className={styles.headerTitle}>Syllabus</h1>
          </div>
          <div className={styles.actions}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.addButton}
              fullWidth={false}
              onClick={handleCreateSyllabus}
            >
              {translations.labels?.addSyllabus || "ADD SYLLABUS"}
            </CustomButton>
          </div>
        </div>

        <CustomMRTTable
          enablePagination="true"
          columns={columns}
          data={data}
          loading={loading}
          pagination={pagination}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          onSearchEnter={(value) => {
            setApiSearch(value);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
          enableSearch={false}
          enableFiltering={false}
        />
      </div>
    </>
  );
};

export default SyllabusList;
